/**
 * A loja e a carteira.
 *
 * A moeda do jogo é o Emblema (◈). Ganha-se vencendo capítulos, escaramuças e
 * partidas online, e gasta-se aqui. Também dá para comprar com dinheiro de
 * verdade pelo Mercado Pago.
 *
 * Nenhum preço desta tela decide nada. O botão manda "quero o item 7"; quanto o
 * item 7 custa, se há estoque e se o saldo dá é sempre o servidor quem responde
 * — o que se vê aqui é a mesma informação, para o jogador não comprar às cegas.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, messageOf } from '../api/client.js';
import { ItemIcon } from '../components/ItemIcon.js';
import { useAuth } from '../auth/AuthContext.js';

interface ShopItem {
  readonly id: number;
  readonly contentType: string;
  readonly contentId: number;
  readonly name: string;
  readonly description: string | null;
  readonly icon: number | null;
  readonly price: number;
  readonly stock: number | null;
  readonly perUserLimit: number | null;
  readonly featured: boolean;
  /** No inventário da conta agora (cai quando a arma vai para uma bolsa). */
  readonly owned: number;
  /** Quantas vezes já comprou — é o que o limite por jogador conta. */
  readonly purchased: number;
  /**
   * O que fazer com ele depois de comprado: levar para a bolsa de um
   * personagem ("batalha") ou usar na ficha dele ("ficha"). Quem decide é o
   * servidor, e ele não põe à venda o que não tem uso.
   */
  readonly use?: 'batalha' | 'ficha' | 'nenhum';
}

interface CoinPack {
  readonly id: number;
  readonly slug: string;
  readonly title: string;
  readonly coins: number;
  readonly bonusCoins: number;
  readonly totalCoins: number;
  readonly priceCents: number;
  readonly currency: string;
}

interface StatementLine {
  readonly amount: number;
  readonly reason: string;
  readonly note: string | null;
  readonly balanceAfter: number;
  readonly createdAt: string;
}

/** O nome de cada motivo do extrato, para quem lê e não escreveu o banco. */
const REASON_LABEL: Record<string, string> = {
  recompensa_capitulo: 'Capítulo vencido',
  recompensa_escaramuca: 'Escaramuça vencida',
  recompensa_online: 'Partida online vencida',
  compra_loja: 'Compra na loja',
  compra_moedas: 'Compra de Emblemas',
  estorno: 'Estorno',
  ajuste_admin: 'Ajuste da administração',
  bonus_inicial: 'Bônus de boas-vindas',
};

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(cents / 100);
}

export function ShopPage({ onOpenAuth }: { readonly onOpenAuth: () => void }): JSX.Element {
  const { user } = useAuth();
  const [items, setItems] = useState<ShopItem[]>([]);
  const [packs, setPacks] = useState<CoinPack[]>([]);
  const [paymentsReady, setPaymentsReady] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [statement, setStatement] = useState<StatementLine[]>([]);
  const [tab, setTab] = useState<'loja' | 'emblemas' | 'extrato'>('loja');
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError('');
    try {
      const [shop, coinPacks] = await Promise.all([
        api.get<{ balance: number | null; items: ShopItem[] }>('/api/economia/loja'),
        api.get<{ configured: boolean; packs: CoinPack[] }>('/api/economia/pacotes'),
      ]);
      setItems(shop.items);
      setBalance(shop.balance);
      setPacks(coinPacks.packs);
      setPaymentsReady(coinPacks.configured);

      if (user) {
        const wallet = await api.get<{ balance: number; statement: StatementLine[] }>(
          '/api/economia/carteira',
        );
        setBalance(wallet.balance);
        setStatement(wallet.statement);
      }
    } catch (e) {
      setError(messageOf(e, 'Não consegui carregar a loja.'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Quem volta do Mercado Pago chega com `?pagamento=...&ref=...` na barra de
   * endereços. Isso é um AVISO, não a confirmação: o crédito só existe quando o
   * webhook do provedor chega ao servidor. Por isso a tela pergunta o estado
   * real em vez de acreditar na URL.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('pagamento');
    const reference = params.get('ref');
    if (!outcome || !reference) return;

    window.history.replaceState({}, '', window.location.pathname);

    if (outcome === 'falha') {
      setError('O pagamento não foi concluído.');
      return;
    }

    setTab('emblemas');
    setNotice('Confirmando o pagamento com o Mercado Pago…');

    let attempts = 0;
    const check = async (): Promise<void> => {
      attempts++;
      try {
        const status = await api.get<{ status: string; coins: number; balance: number }>(
          `/api/economia/pagamento/${encodeURIComponent(reference)}`,
        );
        setBalance(status.balance);

        if (status.status === 'aprovado') {
          setNotice(`Pagamento aprovado — ◈ ${status.coins} Emblemas creditados.`);
          void load();
          return;
        }
        if (['recusado', 'cancelado'].includes(status.status)) {
          setNotice('');
          setError('O pagamento não foi aprovado.');
          return;
        }
      } catch {
        // Uma consulta perdida não é o pagamento perdido; a próxima tenta de novo.
      }

      if (attempts < 20) {
        window.setTimeout(() => void check(), 3000);
      } else {
        setNotice(
          'O pagamento ainda está sendo processado pelo Mercado Pago. '
          + 'Assim que ele confirmar, os Emblemas entram na sua conta.',
        );
      }
    };

    void check();
  }, [load]);

  const buy = async (item: ShopItem) => {
    if (!user) {
      onOpenAuth();
      return;
    }

    setBusy(item.id);
    setError('');
    setNotice('');
    try {
      const result = await api.post<{ balance: number; item: { name: string; quantity: number } }>(
        '/api/economia/comprar',
        { itemId: item.id },
      );
      setBalance(result.balance);
      // A compra vai para o inventário da CONTA; a partida lê a bolsa de cada
      // personagem. Sem dizer isso, a arma comprada parecia sumir.
      setNotice(item.use === 'ficha'
        ? `${result.item.name} comprado. Use-o na ficha de um personagem na aba Comunidade — o reforço é para sempre.`
        : `${result.item.name} comprado. Leve-o para a bolsa de um personagem na aba Comunidade.`);
      await load();
    } catch (e) {
      setError(messageOf(e, 'A compra não foi concluída.'));
    } finally {
      setBusy(null);
    }
  };

  const checkout = async (pack: CoinPack) => {
    if (!user) {
      onOpenAuth();
      return;
    }

    setBusy(-pack.id);
    setError('');
    try {
      const result = await api.post<{ checkoutUrl: string }>('/api/economia/checkout', {
        packId: pack.id,
      });
      // O pagamento acontece no ambiente do provedor. Nenhum dado de cartão
      // passa por este site, e é assim que deve ser.
      window.location.href = result.checkoutUrl;
    } catch (e) {
      setError(messageOf(e, 'Não consegui abrir o pagamento.'));
      setBusy(null);
    }
  };

  if (loading) {
    return <section data-panel="loja"><p className="muted">carregando…</p></section>;
  }

  return (
    <section data-panel="loja" className="shop-shell">
      <header className="shop-header">
        <div>
          <h2>🛒 Loja</h2>
          <p className="muted">Gaste Emblemas em armas e itens para levar às batalhas.</p>
        </div>
        <div className="wallet-chip" title="Seu saldo de Emblemas">
          <span className="wallet-symbol">◈</span>
          <strong>{balance ?? '—'}</strong>
          <span className="wallet-name">Emblemas</span>
        </div>
      </header>

      <nav className="shop-tabs" aria-label="Seções da loja">
        {([
          ['loja', '🗡️ Itens'],
          ['emblemas', '◈ Comprar Emblemas'],
          ['extrato', '🧾 Extrato'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`shop-tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && <p className="importer-error">⚠️ {error}</p>}
      {notice && <p className="importer-notice">✅ {notice}</p>}

      {tab === 'loja' && (
        <div className="shop-grid">
          {items.map((item) => {
            const atLimit = item.perUserLimit !== null && item.purchased >= item.perUserLimit;
            const soldOut = item.stock !== null && item.stock <= 0;
            const tooPoor = balance !== null && balance < item.price;

            return (
              <article key={item.id} className={`shop-card ${item.featured ? 'featured' : ''}`}>
                <div className="shop-card-top">
                  {item.icon !== null && (
                    <ItemIcon icon={item.icon} iconSheet={(item as any).iconSheet} size={32} />
                  )}
                  <h3>{item.name}</h3>
                </div>
                {item.description && <p className="shop-card-desc">{item.description}</p>}
                {/* Para onde a compra vai: sem isto, o jogador compra e não
                    sabe por que o item não aparece na batalha. */}
                <p className="shop-card-use muted">
                  {item.use === 'ficha'
                    ? '⬆ Reforço de ficha — use no personagem, na aba Comunidade'
                    : '🎒 Vai para a bolsa de um personagem, na aba Comunidade'}
                </p>
                <div className="shop-card-foot">
                  <span className="shop-price">◈ {item.price}</span>
                  <button
                    type="button"
                    disabled={busy === item.id || atLimit || soldOut || (user !== null && tooPoor)}
                    onClick={() => void buy(item)}
                    title={
                      atLimit ? 'Você já comprou o máximo deste item'
                        : soldOut ? 'Esgotado'
                          : tooPoor ? 'Emblemas insuficientes'
                            : undefined
                    }
                  >
                    {busy === item.id ? '…'
                      : atLimit ? 'já comprado'
                        : soldOut ? 'esgotado'
                          : 'Comprar'}
                  </button>
                </div>
                {item.owned > 0 && <span className="shop-owned">você tem {item.owned}</span>}
              </article>
            );
          })}
        </div>
      )}

      {tab === 'emblemas' && (
        <div className="packs-area">
          {!paymentsReady && (
            <p className="packs-warning">
              ⚙️ A compra com dinheiro real ainda não foi configurada neste servidor. Um
              administrador precisa definir <code>OZ_MP_ACCESS_TOKEN</code> em{' '}
              <code>api/.env</code> com a credencial do Mercado Pago.
            </p>
          )}

          <div className="packs-grid">
            {packs.map((pack) => (
              <article key={pack.id} className="pack-card">
                <h3>{pack.title}</h3>
                <p className="pack-coins">
                  ◈ {pack.coins.toLocaleString('pt-BR')}
                  {pack.bonusCoins > 0 && (
                    <span className="pack-bonus">+ {pack.bonusCoins.toLocaleString('pt-BR')} bônus</span>
                  )}
                </p>
                <p className="pack-total">{pack.totalCoins.toLocaleString('pt-BR')} no total</p>
                <button
                  type="button"
                  className="pack-buy"
                  disabled={!paymentsReady || busy === -pack.id}
                  onClick={() => void checkout(pack)}
                >
                  {busy === -pack.id ? 'abrindo…' : money(pack.priceCents, pack.currency)}
                </button>
              </article>
            ))}
          </div>

          <p className="packs-note">
            O pagamento acontece no ambiente do Mercado Pago — nenhum dado de cartão passa por
            este site. Os Emblemas entram na conta quando o Mercado Pago confirma.
          </p>
        </div>
      )}

      {tab === 'extrato' && (
        user === null ? (
          <p className="muted">
            <button type="button" className="linklike" onClick={onOpenAuth}>Entre na sua conta</button>
            {' '}para ver o extrato.
          </p>
        ) : statement.length === 0 ? (
          <p className="muted">Nenhum movimento ainda.</p>
        ) : (
          <table className="statement-table">
            <thead>
              <tr><th>Quando</th><th>Motivo</th><th className="num">Valor</th><th className="num">Saldo</th></tr>
            </thead>
            <tbody>
              {statement.map((line, i) => (
                <tr key={`${line.createdAt}-${i}`}>
                  <td className="mono">{new Date(line.createdAt.replace(' ', 'T') + 'Z').toLocaleString('pt-BR')}</td>
                  <td>
                    {REASON_LABEL[line.reason] ?? line.reason}
                    {line.note && <span className="statement-note"> · {line.note}</span>}
                  </td>
                  <td className={`num ${line.amount >= 0 ? 'positive' : 'negative'}`}>
                    {line.amount >= 0 ? '+' : ''}{line.amount}
                  </td>
                  <td className="num mono">{line.balanceAfter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </section>
  );
}
